import { useEffect, useState } from 'react';
import { insuranceService } from 'src/services/insurance';
import type { InsurancePlan } from 'src/services/interfaces/insurance';

/**
 * WP6 — the plan-attribute join. The W2 offering wire carries only
 * `{planId, category, shortName, coverageOptions}` (no attributes/description).
 * B2C reads richer data from `GET /insurances/plans/full` (`getFullPlans`), which
 * authorizes a W2 employee JWT (the W2 PHQ already calls it — `W2Onboarding.tsx`),
 * so the selection cards join `offering.planId === plan.id` client-side with ZERO
 * backend work and reuse the existing B2C `getPlanStats` / `FormPlanDetails`.
 *
 * It MUST be `/full`, not the plain `GET /insurances/plans`: verified against the
 * live backend (2026-07-21) the non-full endpoint returns NEITHER `details` NOR
 * `longDescription` — only `/full` carries `details.{coverage,addons,
 * serviceFrequencies,…}` + `longDescription`, i.e. everything the stats column
 * and the "Show Plan Details" expander render.
 *
 * GRACEFUL ABSENCE is the contract: the map is `{}` until loaded and on ANY
 * failure (unauthorized, network, unexpected shape). The cards MUST render
 * name+price with or without it — the flow never blocks on this fetch.
 */
export type PlanDetailsMap = Record<number, InsurancePlan>;

// Module-level promise cache: one GET shared across every card/screen (and
// re-entries) for the life of the tab.
let cache: Promise<PlanDetailsMap> | null = null;

async function load(): Promise<PlanDetailsMap> {
  try {
    const plans = await insuranceService.getFullPlans();
    const map: PlanDetailsMap = {};
    for (const plan of Array.isArray(plans) ? plans : []) {
      if (plan && typeof plan.id === 'number') map[plan.id] = plan;
    }
    return map;
  } catch {
    return {};
  }
}

export function loadPlanDetails(): Promise<PlanDetailsMap> {
  if (!cache) cache = load();
  return cache;
}

/** Test seam — drop the cached fetch between tests. */
export function resetPlanDetailsCache(): void {
  cache = null;
}

/** Fetch (once, cached) the group's full plan records indexed by id. */
export function usePlanDetails(): PlanDetailsMap {
  const [map, setMap] = useState<PlanDetailsMap>({});
  useEffect(() => {
    let alive = true;
    loadPlanDetails().then((m) => {
      if (alive) setMap(m);
    });
    return () => {
      alive = false;
    };
  }, []);
  return map;
}
